import { useState } from "react";
import { useDispatch } from "react-redux";
import Papa from "papaparse";
import ErrorText from "../../../components/Typography/ErrorText";
import { showNotification } from "../../common/headerSlice";
import api from "../../../utils/axios";
import { triggerRefresh } from "../../../utils/refreshRegistry";

const ERROR_FILE_REQUIRED = "file_required";
const ERROR_INVALID_HEADER = "header_has_invalid_columns";
const expectedHeaders = ["ID", "Name"]

async function addAllSpecialMeeting(e, replace) {
  try {
    const newSpecialMeetings = await api.post("api/special-meetings/all/", e);
    return newSpecialMeetings;
  } catch (error) {
    console.error("Error adding special meetings:", error);
    throw error;
  }
}

function ImportSpecialMeetingsFromCSVModalBody({ closeModal }) {
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageType, setErrorMessageType] = useState("");
  const [file, setFile] = useState();

  const fileReader = new FileReader();

  const readFile = (replace = false) => {
    if (!file) {
      setErrorMessage("File is required!");
      setErrorMessageType(ERROR_FILE_REQUIRED);
      return;
    }
    fileReader.onload = async function (event) {
      const csvOutput = event.target.result;

      // Parse CSV using PapaParse - handles BOM, quoted fields, line endings, etc.
      // `header: true` means PapaParse will parse the first row as headers and return row objects.
      const parseResult = Papa.parse(csvOutput, {
        header: true,
        skipEmptyLines: true,
        // Normalize headers (trim whitespace + strip UTF-8 BOM if present on the first header)
        transformHeader: (header) => header.replace(/^\uFEFF/, "").trim(),
      });

      if (parseResult.errors.length > 0) {
        console.warn("CSV parsing warnings:", parseResult.errors);
      }

      // Validate header row using PapaParse's parsed header list
      const parsedHeaders = (parseResult.meta?.fields || []).map((h) => (h || "").trim());
      const headersMatch =
        parsedHeaders.length === expectedHeaders.length &&
        expectedHeaders.every((expected, index) => parsedHeaders[index] === expected);

      if (!headersMatch) {
        setErrorMessage("Wrong CSV Format!");
        setErrorMessageType(ERROR_INVALID_HEADER);
        return;
      }

      closeModal();
      let specialMeetingImportList = [];

      const rows = parseResult.data || [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] || {};

        // Skip rows where all relevant values are empty
        const isEmpty = expectedHeaders.every((k) => !row[k] || row[k].toString().trim() === "");
        if (isEmpty) continue;

        let newSpecialMeetingObj = {
          name: (row.Name || "").trim(),
        };
        specialMeetingImportList.push(newSpecialMeetingObj);
      }

      // batching because request payload size
      try {
        for (let i = 0; i <specialMeetingImportList.length; i += 25) {
          const specialMeetingSlice = specialMeetingImportList.slice(i, i + 25);
          const replaceAll = i === 0 ? replace : false;
          await addAllSpecialMeeting(specialMeetingSlice, replaceAll);
        }

        dispatch(
          showNotification({
            message:
              specialMeetingImportList.length + " Special Meetings Imported Successfully!",
            status: 1,
          }),
        );
        // Refresh the messages table after import completes
        // Add a small delay to ensure server has processed all batches
        setTimeout(() => {
          triggerRefresh("specialMeetings");
        }, 500);
      } catch (error) {
        console.error("Error importing special meetings:", error);
        dispatch(
          showNotification({
            message: "Failed to import special meetings. Please try again.",
            status: 0,
          }),
        );
      }
    };

    fileReader.readAsText(file);
  };

  const updateFormValue = (e) => {
    setErrorMessage("");
    setErrorMessageType("");
    setFile(e.target.files[0]);
  };

  return (
    <>
      <div className="bg-base-200 rounded-lg p-4 mb-4 text-sm">
        <p className="font-semibold mb-1">Required CSV Format</p>
        <p className="mb-1">
          Header row:{" "}
          <code className="bg-base-300 px-1 rounded text-xs">{expectedHeaders.join(",")}</code>
        </p>
        <table className="table table-xs w-full mt-1">
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="font-mono">ID</td>
              <td>Row identifier (not used during import, but column must be present)</td>
            </tr>
            <tr>
              <td className="font-mono">Name</td>
              <td>Name of the special meeting (e.g. "Summer Conference 2024")</td>
            </tr>
          </tbody>
        </table>
      </div>

      <input type="file" accept=".csv" onChange={updateFormValue} />

      {errorMessageType === ERROR_INVALID_HEADER ? (
        <ErrorText styleClass="mt-5">
          Please follow correct CSV format!
          <br />
          (First row must be: {expectedHeaders.join(', ')})
        </ErrorText>
      ) : (
        <ErrorText styleClass="mt-5">{errorMessage}</ErrorText>
      )}

      <div className="modal-action">
        <button className="btn btn-ghost" onClick={() => closeModal()}>
          Cancel
        </button>
        <button
          className="btn btn-primary px-6"
          onClick={() => readFile(false)}
        >
          Import
        </button>
        {/* <button className="btn btn-error px-6" onClick={() => readFile(true)}>
          Import and Replace all
        </button> */}
      </div>
    </>
  );
}

export default ImportSpecialMeetingsFromCSVModalBody;
